import React from 'react';
import { formatDataBlock, parseQuestionStructure } from '../utils/parseQuestionStructure';

function QuestionBody({ structure, accentColor }) {
  if (structure.type === 'simple') {
    return <p className="question-text">{structure.text}</p>;
  }

  if (structure.type === 'passage') {
    return (
      <>
        <div className="passage-block">
          <p className="passage-text">{structure.passageText}</p>
        </div>
        <p className="question-stem">{structure.questionStem}</p>
      </>
    );
  }

  return (
    <>
      {structure.premise ? <p className="question-premise">{structure.premise}</p> : null}
      {structure.dataBlock ? (
        <div className="question-data-block" style={{ borderLeftColor: accentColor }}>
          <pre className="data-pre">{formatDataBlock(structure.dataBlock)}</pre>
        </div>
      ) : null}
      <p className="question-stem">{structure.questionStem}</p>
    </>
  );
}

export default function StandardMCQRenderer({
  question,
  value,
  onChange,
  accentColor = 'var(--apt-primary)',
  showExplanation = false,
  correctAnswer = null
}) {
  if (!question) return null;
  const structure = parseQuestionStructure(question.question || '');

  const optionEntries = Array.isArray(question.options)
    ? question.options.map((opt) => [opt.key, opt.text])
    : Object.entries(question.options || {});

  return (
    <div className="assessment-shell">
      <div className="question-card rounded-2xl border border-white/20 bg-slate-900/55 backdrop-blur-xl p-5">
        <QuestionBody structure={structure} accentColor={accentColor} />
        {question.mediaType === 'ascii' && question.media ? (
          <pre className="mt-4 rounded-lg bg-black/40 border border-white/15 p-3 text-xs md:text-sm font-mono overflow-auto">
            {question.media}
          </pre>
        ) : null}
        {question.mediaType === 'svg' && question.media ? (
          <div className="mt-4 rounded-lg bg-white p-3 text-black" dangerouslySetInnerHTML={{ __html: question.media }} />
        ) : null}
      </div>

      <div className="mt-4 space-y-3 options-list">
        {optionEntries.map(([key, text]) => {
          let state = '';
          if (showExplanation && correctAnswer) {
            if (key === correctAnswer) state = 'correct';
            else if (key === value) state = 'wrong';
          } else if (value === key) {
            state = 'selected';
          }

          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                if (!showExplanation && onChange) onChange(key);
              }}
              disabled={showExplanation}
              className={`option-btn w-full text-left px-4 py-3 rounded-xl border transition ${state === 'selected' ? 'selected border-violet-300 bg-gradient-to-r from-violet-600 to-purple-700 text-white' : state === 'correct' ? 'correct border-emerald-300 bg-emerald-600/35 text-white' : state === 'wrong' ? 'wrong border-red-300 bg-red-600/35 text-white' : 'border-white/25 bg-slate-800/60 text-white hover:border-violet-400/70 hover:bg-slate-700/70'}`}
            >
              <span className="font-semibold mr-2">{key}.</span>
              <span>{text}</span>
              {state === 'correct' ? <span className="option-icon ml-2">✓</span> : null}
              {state === 'wrong' ? <span className="option-icon ml-2">✕</span> : null}
            </button>
          );
        })}
      </div>

      {showExplanation && question.explanation ? (
        <div className="explanation-block mt-4 rounded-xl border border-white/20 bg-slate-900/50 p-4">
          <span className="explanation-label text-xs uppercase tracking-wide text-white/70">Explanation</span>
          <p className="explanation-text mt-2 text-sm leading-7 text-white/85">{question.explanation}</p>
        </div>
      ) : null}
    </div>
  );
}
